import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { RsCountryCode } from '../../constants/rsCountryCodes';
import RsMonthNav from './RsMonthNav';
import RsCountrySelect from './RsCountrySelect';
import RsBoardQuickGuide from './RsBoardQuickGuide';
import RsShareUrlSection from './RsShareUrlSection';
import styles from './ReservationBoardCalendar.module.css';

type RsBoardCalendarChromeProps = {
  boardId: number;
  boardName: string;
  isBoardOwner: boolean;
  year: number;
  month: number;
  isViewingCurrentMonth: boolean;
  onPrevMonth: () => void;
  onToday: () => void;
  onNextMonth: () => void;
  holidayCountryCode: RsCountryCode;
  onHolidayCountryChange: (code: RsCountryCode) => void;
  countryUpdatePending: boolean;
  shareUrl: string | null | undefined;
  onIssueShare: () => void;
  onRevokeShare: () => void;
  issuingShare: boolean;
  revokingShare: boolean;
  t: TFunction;
};

/** 月ナビ・国・使い方の sticky 帯。共有URLは帯の外（展開でカレンダーを食わない） */
export default function RsBoardCalendarChrome({
  boardId,
  boardName,
  isBoardOwner,
  year,
  month,
  isViewingCurrentMonth,
  onPrevMonth,
  onToday,
  onNextMonth,
  holidayCountryCode,
  onHolidayCountryChange,
  countryUpdatePending,
  shareUrl,
  onIssueShare,
  onRevokeShare,
  issuingShare,
  revokingShare,
  t,
}: RsBoardCalendarChromeProps) {
  const backLabel = t('reservation.board.backToList', { defaultValue: '予約表一覧' });

  return (
    <>
      <div className={styles.calendarStickyChrome} data-rs-calendar-toolbar>
        <div className={styles.calendarToolbar}>
          {isBoardOwner && (
            <Link
              to="/reservations"
              className={`btn btn-outline-secondary btn-sm ${styles.calendarBackBtn}`}
              aria-label={backLabel}
              title={backLabel}
            >
              <i className={`fas fa-list ${styles.calendarBackBtnIcon}`} aria-hidden />
              <span className="d-none d-lg-inline">{backLabel}</span>
            </Link>
          )}
          {/* 表名はレイアウトの share バー／document title が正。ここは SR 用のみ */}
          <h2 className="visually-hidden">{boardName}</h2>
          <RsMonthNav
            year={year}
            month={month}
            isViewingCurrentMonth={isViewingCurrentMonth}
            onPrev={onPrevMonth}
            onToday={onToday}
            onNext={onNextMonth}
          />
          <div className={`${styles.calendarStickyActions} ${styles.calendarToolbarActions}`}>
            <div className={styles.calendarCountryWrap}>
              <RsCountrySelect
                id={`rs-holiday-country-${boardId}`}
                value={holidayCountryCode}
                onChange={onHolidayCountryChange}
                disabled={countryUpdatePending}
                compact
                t={t}
                hintKey={
                  isBoardOwner
                    ? 'reservation.board.countryCodeHintOwner'
                    : 'reservation.board.countryCodeHintGuest'
                }
                hintDefault={
                  isBoardOwner
                    ? '選択した国の祝日がカレンダーに表示されます。変更は予約表に保存されます。'
                    : '表示する祝日の国を選択できます（このブラウザのみ記憶されます）。'
                }
              />
            </div>
            <RsBoardQuickGuide isBoardOwner={isBoardOwner} />
          </div>
        </div>
      </div>
      {isBoardOwner ? (
        <div className={styles.shareUrlChromeSlot} data-rs-share-slot>
          <RsShareUrlSection
            shareUrl={shareUrl}
            onIssue={onIssueShare}
            onRevoke={onRevokeShare}
            issuing={issuingShare}
            revoking={revokingShare}
          />
        </div>
      ) : null}
    </>
  );
}
